import React, { useEffect, useState, useCallback } from "react";
import { Plus, Loader2, RefreshCw, Pencil, Trash2, X, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PRIMARY = "#F97316";

/**
 * Reusable admin CRUD panel styled identically to the Customers module.
 * config: { endpoint, title, required, columns:[{key,label,render?}],
 *           fields:[{key,label,type?,options?,placeholder?}], searchKeys:[] }
 */
export default function ResourceCrud({ config }) {
  const { endpoint, title, columns, fields, searchKeys, required = "name" } = config;
  const blank = fields.reduce((a, f) => ({ ...a, [f.key]: "" }), {});

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get(endpoint)).data); }
    catch { toast.error(`Could not load ${title.toLowerCase()}.`); }
    finally { setLoading(false); }
  }, [endpoint, title]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    setRows((p) => p.filter((r) => r.id !== id));
    try { await api.delete(`${endpoint}/${id}`); toast.success("Deleted."); }
    catch { toast.error("Delete failed."); load(); }
  };

  const filtered = rows.filter((r) =>
    (searchKeys || ["name"]).map((k) => r[k] || "").join(" ").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4" data-testid={`${endpoint.replace("/", "")}-module`}>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-navy">{title} <span className="text-slate-400 text-lg font-normal">({rows.length})</span></h1>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${title.toLowerCase()}…`}
            className="pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-cool" data-testid={`${endpoint.replace("/", "")}-search`} />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-lg border border-slate-200 bg-white hover:border-slate-300" data-testid={`${endpoint.replace("/", "")}-refresh`}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
          <button onClick={() => setEditing({ ...blank })}
            className="px-4 py-2 rounded-lg text-white text-sm font-bold flex items-center gap-1.5 hover:opacity-90"
            style={{ background: PRIMARY }} data-testid={`${endpoint.replace("/", "")}-add`}>
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-slate-400 py-16 text-sm">Nothing here yet — add your first one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  {columns.map((c) => <th key={c.key} className="text-left px-4 py-3 font-bold">{c.label}</th>)}
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`${endpoint.replace("/", "")}-row-${r.id}`}>
                    {columns.map((c, i) => (
                      <td key={c.key} className={`px-4 py-3 ${i === 0 ? "font-semibold text-slate-800" : "text-slate-600"} truncate max-w-[220px]`}>
                        {c.render ? c.render(r) : (r[c.key] || "")}
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setEditing(r)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`${endpoint.replace("/", "")}-edit-${r.id}`}><Pencil className="w-4 h-4 text-slate-500" /></button>
                        <button onClick={() => remove(r.id)} className="p-1.5 rounded hover:bg-red-50" data-testid={`${endpoint.replace("/", "")}-delete-${r.id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && <ResourceModal endpoint={endpoint} required={required} fields={fields} blank={blank} record={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ResourceModal({ endpoint, required, fields, blank, record, onClose, onSaved }) {
  const isEdit = !!record.id;
  const [f, setF] = useState({ ...blank, ...record });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { const h = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);

  const save = async () => {
    if (required && !(f[required] || "").trim()) { toast.error(`${required} is required.`); return; }
    setBusy(true);
    try {
      if (isEdit) await api.put(`${endpoint}/${record.id}`, f);
      else await api.post(endpoint, f);
      toast.success(isEdit ? "Updated." : "Added.");
      onSaved();
    } catch (e) { toast.error("Save failed: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const eid = endpoint.replace("/", "");
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid={`${eid}-modal`}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">{isEdit ? "Edit" : "New"}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="space-y-3">
          {fields.map((fld, idx) => (
            fld.type === "textarea" ? (
              <textarea key={fld.key} value={f[fld.key]} onChange={(e) => set(fld.key, e.target.value)} placeholder={fld.placeholder || fld.label} rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid={`${eid}-f-${fld.key}`} />
            ) : fld.type === "select" ? (
              <select key={fld.key} value={f[fld.key]} onChange={(e) => set(fld.key, e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white" data-testid={`${eid}-f-${fld.key}`}>
                <option value="">{fld.label}…</option>
                {(fld.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input key={fld.key} autoFocus={idx === 0} value={f[fld.key]} onChange={(e) => set(fld.key, e.target.value)} placeholder={fld.placeholder || (fld.key === required ? `${fld.label} *` : fld.label)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid={`${eid}-f-${fld.key}`} />
            )
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600">Cancel</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 rounded-lg text-white text-sm font-bold flex items-center gap-2 disabled:opacity-60" style={{ background: PRIMARY }} data-testid={`${eid}-save`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {isEdit ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}
